import React, { useState, useEffect } from "react";
import axios from "axios";
import { getSession } from "../utils/session";
import { API_URL, ORIGEN_API } from "../services/api";
import "./PersonalizedRoutine.css";

const ENDPOINT =
  `${API_URL}/personal-routines-ai/generate`;

// Utilidad robusta para extraer campos
const get = (obj, ...keys) => keys.reduce((a, k) => a ?? obj[k], undefined);

export default function GeneralRoutineAI({ peso, altura, objetivo }) {
  const [routine, setRoutine] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hechos, setHechos] = useState({});
  const [debug, setDebug] = useState({});

  useEffect(() => {
    async function fetchRoutine() {
      setLoading(true);
      setError("");
      setRoutine([]);
      setDebug({});
      try {
        const { user, token } = await getSession();
        const userId = user?.id || user?._id;
        if (!userId) throw new Error("Usuario no encontrado. Reintenta sesión.");
        const res = await axios.post(ENDPOINT, {
          userId,
          peso,
          altura,
          objetivo,
        }, { headers: { Authorization: `Bearer ${token}` } });

        // SOPORTE: objeto con key rutina o array directo
        let arr = [];
        if (Array.isArray(res.data.rutina)) arr = res.data.rutina;
        else if (Array.isArray(res.data)) arr = res.data;
        else if (Array.isArray(res.data.routine)) arr = res.data.routine;
        setDebug({ raw: res.data, arr });

        setRoutine(arr);
        setLastUpdated(res.data.lastUpdated || null);
      } catch (err) {
        setError(
          err?.response?.data?.error ||
            err?.message ||
            "Error cargando rutina."
        );
        setDebug(prev => ({ ...prev, error: err }));
      } finally {
        setLoading(false);
      }
    }
    fetchRoutine();
  }, [peso, altura, objetivo]);

  // Marcar rutina completa como realizada (suma las calorías totales de la rutina)
  const handleDone = async (idx) => {
    const rutina = routine[idx];
    const nombre = get(rutina, "nombre", "name", "Rutina");
    const calorias = Number(get(rutina, "calorias totales", "calorias", "calorias quemadas", "calories")) || (
      Array.isArray(rutina.ejercicios)
        ? rutina.ejercicios.reduce((sum, ej) => sum + Number(get(ej, "calorias quemadas", "calorias", "calories") || 0), 0)
        : 0
    );
    try {
      const { user, token } = await getSession();
      const userId = user?.id || user?._id;
      if (!calorias || calorias <= 0) {
        alert("La rutina debe tener calorías válidas.");
        return;
      }
      await axios.post(
        `${API_URL}/exercises`,
        {
          name: nombre,
          caloriesBurned: calorias,
          userId,
          date: new Date(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setHechos((prev) => ({ ...prev, [idx]: true }));
    } catch (err) {
      alert(
        "Error al guardar la rutina: " +
          (err.response?.data?.message || err.message)
      );
    }
  };

  return (
    <section className="rutina-semana">
      <div className="rutina-cabecera">
        <h2 className="rutina-seccion">Sesiones de la semana</h2>
        {lastUpdated && (
          <span className="rutina-fecha">
            Preparada el {new Date(lastUpdated).toLocaleDateString("es-ES")}
          </span>
        )}
      </div>

      {loading ? (
        <div className="app-estado">Cargando rutina...</div>
      ) : error ? (
        <div className="app-estado app-estado-error">{error}</div>
      ) : routine.length === 0 ? (
        <div className="app-estado">No hay rutina disponible aún.</div>
      ) : (
        <div className="rutina-sesiones">
          {routine.map((rut, i) => (
            <article key={i} className="app-panel rutina-sesion" style={{ "--d": `${i * 80}ms` }}>
              <header className="rutina-sesion-cabecera">
                <h3>{rut.nombre}</h3>
                <span className="rutina-etiqueta">
                  {get(rut, "tiempo total", "tiempo", "duration")}
                </span>
              </header>

              <ul className="rutina-ejercicios">
                {Array.isArray(rut.ejercicios) &&
                  rut.ejercicios.map((ej, idx) => (
                    <li key={idx}>
                      {ej.gif && (
                        <img className="rutina-ej-gif" src={ORIGEN_API + ej.gif} alt="" loading="lazy" />
                      )}
                      <div className="rutina-ej-texto">
                        <span className="rutina-ej-nombre">{get(ej, "nombre", "name")}</span>
                        <span className="rutina-ej-desc">
                          {get(ej, "descripcion", "description")} · {get(ej, "dificultad")}
                        </span>
                      </div>
                      <span className="rutina-ej-carga">
                        {get(ej, "series")} × {get(ej, "repeticiones")} · {get(ej, "descanso")}
                      </span>
                    </li>
                  ))}
              </ul>

              <footer className="rutina-sesion-pie">
                <span className="rutina-kcal">
                  {get(rut, "calorias totales", "calorias")} kcal
                </span>
                {!hechos[i] ? (
                  <button className="app-btn app-btn-sm" onClick={() => handleDone(i)}>
                    Lo hice hoy
                  </button>
                ) : (
                  <span className="rutina-hecha">Registrada como hecha</span>
                )}
              </footer>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
